import { zodResolver } from "@hookform/resolvers/zod";
import { GOAL_OPTIONS, GoalTargetCorpusLabelMap } from "creso-core/constants";
import {
  useCreateGoal,
  useDefaultGoalName,
  useGoal,
  useUpdateGoal,
} from "creso-core/hooks";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  cn,
  Form,
  FormField,
  FormInput,
  FormItem,
  FormLabel,
  NumberCounterInput,
  ScrollArea,
  V2Loader,
} from "web-design-system";
import { number, object, string, z } from "zod";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { ArrowLeft, LucideGoal, LucideIndianRupee } from "lucide-react";
import Amount from "@funds/components/atoms/amount";
import { calculateFutureValue, convertToCurrency } from "creso-core/utils";
import { handleError } from "@/utils/error";
import { useNavigate } from "@tanstack/react-router";
import omit from "lodash/omit";
import { Goal } from "creso-core/types";
import { useTheme } from "@/theme";
import { GoalIcon } from "web-core";
import {
  GoalIllustration,
  currencyStepper,
  durationDisplayFormat,
  formatDuration,
} from "../../config";
import { LinkHoldings } from "./link-holdings";

const GoalFormSchema = object({
  id: number().optional(),
  client: number().min(1),
  name: string().min(1),
  category: string(),
  target_corpus: string(),
  duration_months: number(),
  expected_inflation: string(),
});

const DEFAULT_TARGETS = {
  HOUSE: "10000000",
  CAR: "1000000",
  MARRIAGE: "1000000",
  CHILD_EDU: "10000000",
  RETIREMENT: "10000000",
  CUSTOM: "1000000",
};

export function GoalBuilder(props: {
  clientId: number;
  goalId?: number;
  state?: "create" | "rename" | "link";
  goalType?: (typeof GOAL_OPTIONS)[number]["value"];
}) {
  const [uiState, setUIState] = useState(props.state || "create");

  const { data: goal, isLoading: isLoadingGoal } = useGoal(
    { id: props.goalId as number },
    {
      enabled: !!props.goalId,
    }
  );

  const defaultGoalNameGenerator = useDefaultGoalName("");

  const defaultGoalName = useMemo(
    () => goal?.name || defaultGoalNameGenerator(props.goalType as string),
    [goal, props.goalType, defaultGoalNameGenerator]
  );

  const goalType = goal?.category || props.goalType || "CUSTOM";

  const form = useForm({
    resolver: zodResolver(GoalFormSchema),
    values: {
      id: goal?.id,
      client: props.clientId,
      name: defaultGoalName,
      category: goalType,
      target_corpus: goal?.target_corpus || DEFAULT_TARGETS[goalType],
      duration_months: goal?.duration_months || 12,
      expected_inflation: goal?.expected_inflation || "6",
    },
  });

  const { mutateAsync: updateGoal, isPending: isUpdatingGoal } =
    useUpdateGoal();
  const { mutateAsync: createGoal, isPending: isCreatingGoal } =
    useCreateGoal();

  const [totalValueLinkedHoldings, setTotalValueLinkedHoldings] = useState(0);

  const navigate = useNavigate();

  const close = useCallback(
    () =>
      navigate({
        to: ".",
        search: (prev) =>
          omit(prev, ["client_id", "goal_id", "state", "goal_type"]),
      }),
    [navigate]
  );

  const saveGoal = useCallback(
    async (data: z.infer<typeof GoalFormSchema>) => {
      const { id, ...payload } = data;
      if (id) {
        await updateGoal({
          id,
          payload,
        });
      } else {
        const goal = await createGoal(payload);
        navigate({
          to: ".",
          search: (prev) => ({
            ...prev,
            goal_id: goal.id,
            state: "rename",
          }),
          replace: true,
        });
      }
    },
    [updateGoal, createGoal, navigate]
  );

  const getHeaderText = () => {
    if (uiState === "link") {
      return `Link holdings to ${goal?.name}`;
    }
    return goal?.name || defaultGoalName;
  };
  const ref = useRef<{ linkHoldings: () => Promise<void> }>(null);

  const handleLinkHoldings = useCallback(async () => {
    try {
      await ref.current?.linkHoldings();
      close();
    } catch (error) {
      handleError(error, "Failed to link holdings");
    }
  }, [close]);

  const isLoading = isLoadingGoal;

  const { currentTheme = "light" } = useTheme();

  const isPending = useMemo(
    () => isUpdatingGoal || isCreatingGoal,
    [isUpdatingGoal, isCreatingGoal]
  );

  const illustration = GoalIllustration[currentTheme][goalType];

  return (
    <V2Loader
      isLoading={isLoading}
      className="size-full flex-0 flex flex-col overflow-hidden"
    >
      {/** Goals header */}
      <div className="flex min-h-20 items-center p-6 pb-5">
        <Button
          className={cn(
            uiState === "create" && "hidden",
            "shrink-0 -ml-2 mr-2"
          )}
          variant="ghost"
          size="icon"
          onClick={() =>
            setUIState((s) => (s === "rename" ? "create" : "rename"))
          }
        >
          <ArrowLeft />
        </Button>
        <div className="mr-3">
          <GoalIcon category={goalType} />
        </div>
        <span className="text-base font-semibold">{getHeaderText()}</span>
      </div>

      {/** Goals body */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-6 flex flex-col flex-1">
          <ConditionalRenderer condition={uiState !== "link"}>
            <Form {...form}>
              <div
                className={cn(
                  "border relative overflow-hidden border-border rounded-md mb-3",
                  uiState === "rename" && "pb-8"
                )}
              >
                <img className="size-full" src={illustration} />
                <div
                  className={cn(
                    "p-3 absolute bottom-0 gap-2 left-0 right-0 items-center rounded-b-md bg-constructive-foreground hidden",
                    uiState === "rename" && "flex"
                  )}
                >
                  <LucideGoal className="size-4 text-constructive" />
                  {goal && (
                    <span className="text-sm font-normal">
                      In {formatDuration(goal.duration_months)} save
                    </span>
                  )}
                  {goal && (
                    <span className="font-semibold text-sm ml-auto">
                      <Amount precision={0}>
                        {calculateFutureValue(
                          Number(goal.target_corpus),
                          Number(goal.expected_inflation),
                          goal.duration_months
                        )}
                      </Amount>
                    </span>
                  )}
                </div>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                className="flex flex-col flex-1 gap-5"
              >
                <ConditionalRenderer condition={uiState === "create"}>
                  <FormField
                    control={form.control}
                    name="target_corpus"
                    render={({ field }) => (
                      <FormItem className="flex space-y-0 flex-row justify-between items-center">
                        <div className="flex flex-col gap-1">
                          <FormLabel className="!font-medium">
                            {GoalTargetCorpusLabelMap[goalType]}&nbsp;
                          </FormLabel>
                          <span className="text-xs text-muted-foreground">
                            In today&apos;s value
                          </span>
                        </div>
                        <div className="w-48">
                          <NumberCounterInput
                            displayFormatter={(v) => convertToCurrency(v)}
                            step={currencyStepper}
                            min={0}
                            precision={0}
                            {...field}
                            value={Number(field.value)}
                            onChange={(v) => field.onChange(v.toString())}
                          />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="duration_months"
                    render={({ field }) => (
                      <FormItem className="flex flex-row justify-between items-center">
                        <FormLabel className="!font-medium">Duration</FormLabel>
                        <div className="w-48">
                          <NumberCounterInput
                            displayFormatter={durationDisplayFormat}
                            step={1}
                            min={0.1}
                            precision={2}
                            {...field}
                            value={field.value / 12}
                            onChange={(v) => {
                              field.onChange(Math.round(v * 12));
                            }}
                          />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="expected_inflation"
                    render={({ field }) => (
                      <FormItem className="flex flex-row justify-between items-center">
                        <FormLabel className="!font-medium">
                          Expected Inflation
                        </FormLabel>
                        <div className="w-48">
                          <NumberCounterInput
                            {...field}
                            displayFormatter={(v) => `${v}%`}
                            precision={1}
                            step={0.5}
                            min={0}
                            value={Number(field.value)}
                            onChange={(v) => field.onChange(v.toString())}
                          />
                        </div>
                      </FormItem>
                    )}
                  />
                </ConditionalRenderer>
                <ConditionalRenderer condition={uiState === "rename"}>
                  <FormField
                    name="name"
                    control={form.control}
                    render={({ field }) => <FormInput {...field} />}
                  />
                </ConditionalRenderer>
              </form>
            </Form>
          </ConditionalRenderer>
          <ConditionalRenderer condition={uiState === "link"}>
            <LinkHoldings
              onChange={setTotalValueLinkedHoldings}
              goal={goal as Goal}
              ref={ref}
            />
          </ConditionalRenderer>
        </div>
      </ScrollArea>

      {/** Goals footer */}
      <div className="flex flex-row shrink-0 mt-auto h-20 items-center p-5 border-t border-border justify-between">
        <div className="flex flex-row w-full items-center justify-between">
          <ConditionalRenderer condition={uiState === "create"}>
            <div className={cn("flex-row flex gap-3")}>
              <div className="size-9 bg-secondary rounded-md flex items-center justify-center">
                <LucideGoal className="size-6" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-sm">
                  <Amount precision={0}>
                    {calculateFutureValue(
                      Number(form.watch("target_corpus")),
                      Number(form.watch("expected_inflation")),
                      form.watch("duration_months")
                    )}
                  </Amount>
                </span>
                <span className="text-xs text-muted-foreground">
                  Future value
                </span>
              </div>
            </div>
            <Button
              type="button"
              onClick={form.handleSubmit(async (data) => {
                try {
                  await saveGoal(data);
                  setUIState("rename");
                } catch (e) {
                  handleError(e, "Failed to save goal");
                }
              })}
              className={cn("hidden", uiState === "create" && "flex")}
              disabled={isPending}
            >
              {goal?.id ? "Update Goal" : "Create Goal"}
            </Button>
          </ConditionalRenderer>
          <ConditionalRenderer condition={uiState === "rename"}>
            <Button
              variant="outline"
              type="button"
              onClick={form.handleSubmit(async (data) => {
                try {
                  await saveGoal(data);
                  setUIState("link");
                } catch (e) {
                  handleError(e, "Failed to save goal");
                }
              })}
            >
              Link holdings
            </Button>
            <Button
              className="self-end"
              disabled={isPending}
              onClick={form.handleSubmit(async (data) => {
                try {
                  await saveGoal(data);
                  close();
                } catch (e) {
                  handleError(e, "Failed to save goal");
                }
              })}
            >
              Done
            </Button>
          </ConditionalRenderer>
          <ConditionalRenderer condition={uiState === "link"}>
            <div className="flex flex-1 gap-2">
              <div className="size-9 bg-secondary rounded-md flex items-center justify-center">
                <LucideIndianRupee className="size-5" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-sm">
                  <Amount precision={0}>{totalValueLinkedHoldings}</Amount>
                </span>
                <span className="text-xs text-muted-foreground">
                  Total value
                </span>
              </div>
            </div>
            <Button className="self-end" onClick={handleLinkHoldings}>
              Link selected
            </Button>
          </ConditionalRenderer>
        </div>
      </div>
    </V2Loader>
  );
}
